/**
 * CTRL_04 Configuration Panel - Explainability & Transparency
 */
import React, { useState, useEffect } from 'react';
import { Card, Switch, Collapse, Button, Alert, Spin, Select, Slider, Form, Checkbox } from 'antd';
import { SaveOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { getControlConfig, saveControlConfig } from '../api/trustgridApi';
import { showError, showSuccess } from '../../../utils/notifications';

const { Panel } = Collapse;
const { Option } = Select;

const EXPLAINABILITY_METHODS = [
  { value: 'SHAP', label: 'SHAP', description: 'SHapley Additive exPlanations' },
  { value: 'LIME', label: 'LIME', description: 'Local Interpretable Model-agnostic Explanations' },
  { value: 'attention', label: 'Attention Mechanisms', description: 'For neural networks' },
  { value: 'gradient', label: 'Gradient-based', description: 'Integrated gradients, saliency maps' },
  { value: 'counterfactual', label: 'Counterfactual', description: 'What-if scenarios' }
];

function CTRL_04_ConfigPanel({ project, projectId, controlCode = 'CTRL_04' }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [configData, setConfigData] = useState({
    enabled: false,
    required_explainability_methods: ['SHAP', 'LIME'],
    require_confidence_scores: true,
    minimum_confidence_threshold: 70,
    customer_facing_only: false,
    explanation_quality_threshold: 60
  });

  useEffect(() => {
    if (projectId) {
      fetchConfig();
    }
  }, [projectId]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await getControlConfig(projectId, controlCode);
      if (res.data) {
        setConfig(res.data);
        setConfigData({
          enabled: res.data.enabled || false,
          ...res.data.config
        });
      }
    } catch (error) {
      console.error('Failed to fetch config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        project_id: projectId,
        control_code: controlCode,
        enabled: configData.enabled,
        config: {
          required_explainability_methods: configData.required_explainability_methods,
          require_confidence_scores: configData.require_confidence_scores,
          minimum_confidence_threshold: configData.minimum_confidence_threshold,
          customer_facing_only: configData.customer_facing_only,
          explanation_quality_threshold: configData.explanation_quality_threshold
        }
      };

      await saveControlConfig(projectId, controlCode, payload);
      showSuccess('Configuration saved successfully');
      fetchConfig();
    } catch (error) {
      showError(error);
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (field, value) => {
    setConfigData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px' }}><Spin size="large" /></div>;
  }

  return (
    <Card>
      <div style={{ marginBottom: '20px' }}>
        <h3>CTRL_04: Explainability & Transparency Configuration</h3>
        <p style={{ color: '#666' }}>
          Ensure AI decisions are explainable to affected users through SHAP, LIME, or other methods
        </p>
      </div>

      <Collapse defaultActiveKey={configData.enabled ? ['1', '2'] : ['1']}>
        <Panel header="1. Enable Explainability Control" key="1">
          <Switch
            checked={configData.enabled}
            onChange={(checked) => updateConfig('enabled', checked)}
            checkedChildren="Enabled"
            unCheckedChildren="Disabled"
          />
          <p style={{ marginTop: '12px', color: '#666' }}>
            When enabled, inference events will be checked for explainability metadata
          </p>
          <Alert
            message="About Explainability"
            description="GDPR Art 22 and EU AI Act require AI decisions to be explainable. Use SHAP, LIME, or attention mechanisms to provide transparent reasoning."
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
            style={{ marginTop: '12px' }}
          />
        </Panel>

        {configData.enabled && (
          <Panel header="2. Explainability Configuration" key="2">
            <Form layout="vertical">
              <Form.Item label="Accepted Explainability Methods">
                <Checkbox.Group
                  value={configData.required_explainability_methods || []}
                  onChange={(value) => updateConfig('required_explainability_methods', value)}
                  style={{ width: '100%' }}
                >
                  {EXPLAINABILITY_METHODS.map(method => (
                    <div key={method.value} style={{ marginBottom: '12px' }}>
                      <Checkbox value={method.value}>
                        <strong>{method.label}</strong> - {method.description}
                      </Checkbox>
                    </div>
                  ))}
                </Checkbox.Group>
                <p style={{ marginTop: '8px', color: '#666', fontSize: '13px' }}>
                  Methods that satisfy explainability requirements
                </p>
              </Form.Item>

              <Form.Item label="Confidence Score Requirements">
                <div style={{ marginBottom: '16px' }}>
                  <Switch
                    checked={configData.require_confidence_scores}
                    onChange={(checked) => updateConfig('require_confidence_scores', checked)}
                    checkedChildren="Required"
                    unCheckedChildren="Optional"
                    style={{ marginRight: '12px' }}
                  />
                  <span>Require confidence scores for explanations</span>
                </div>

                {configData.require_confidence_scores && (
                  <div>
                    <label>Minimum Confidence Threshold: {configData.minimum_confidence_threshold}%</label>
                    <Slider
                      min={0}
                      max={100}
                      value={configData.minimum_confidence_threshold}
                      onChange={(value) => updateConfig('minimum_confidence_threshold', value)}
                      marks={{ 0: '0%', 50: '50%', 70: '70%', 100: '100%' }}
                      style={{ marginTop: '8px' }}
                    />
                  </div>
                )}
              </Form.Item>

              <Form.Item label="Scope">
                <Switch
                  checked={configData.customer_facing_only}
                  onChange={(checked) => updateConfig('customer_facing_only', checked)}
                  checkedChildren="Customer-facing only"
                  unCheckedChildren="All models"
                />
                <p style={{ marginTop: '8px', color: '#666', fontSize: '13px' }}>
                  Apply explainability requirement only to customer-facing models
                </p>
              </Form.Item>

              <Form.Item label={`Explanation Quality Threshold: ${configData.explanation_quality_threshold}%`}>
                <Slider
                  min={0}
                  max={100}
                  value={configData.explanation_quality_threshold}
                  onChange={(value) => updateConfig('explanation_quality_threshold', value)}
                  marks={{ 0: '0%', 50: '50%', 60: '60%', 100: '100%' }}
                />
                <p style={{ marginTop: '8px', color: '#666', fontSize: '13px' }}>
                  Minimum quality score for generated explanations (if available)
                </p>
              </Form.Item>
            </Form>
          </Panel>
        )}
      </Collapse>

      {configData.enabled && (
        <Alert
          message="Configuration Summary"
          description={
            <div>
              <div><strong>Accepted Methods:</strong> {configData.required_explainability_methods?.join(', ') || 'None'}</div>
              <div><strong>Confidence Required:</strong> {configData.require_confidence_scores ? `Yes (≥${configData.minimum_confidence_threshold}%)` : 'No'}</div>
              <div><strong>Scope:</strong> {configData.customer_facing_only ? 'Customer-facing only' : 'All models'}</div>
              <div><strong>Quality Threshold:</strong> {configData.explanation_quality_threshold}%</div>
            </div>
          }
          type="info"
          showIcon
          style={{ marginTop: '16px' }}
        />
      )}

      <Button
        type="primary"
        icon={<SaveOutlined />}
        onClick={handleSave}
        loading={saving}
        style={{ marginTop: '16px' }}
        size="large"
      >
        Save Configuration
      </Button>
    </Card>
  );
}

export default CTRL_04_ConfigPanel;
